import { useEffect, useState } from "react";
import { Download, X, Smartphone, Zap } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "cria_install_banner_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 3; // 3 days

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function recentlyDismissed() {
  try {
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!at) return false;
    return Date.now() - at < DISMISS_TTL_MS;
  } catch { return false; }
}

export default function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (recentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner 3s after prompt becomes available
      setTimeout(() => setVisible(true), 3000);
    };
    const onInstalled = () => { setVisible(false); setDeferredPrompt(null); };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);

    // Fallback: show banner after 3s even if beforeinstallprompt hasn't fired
    // (iOS never fires it; some Chrome sessions delay it until user engagement)
    const fallback = setTimeout(() => setVisible(true), 3000);

    return () => {
      clearTimeout(fallback);
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (isIOS() && !isStandalone()) { setShowIOS(true); return; }
    if (!deferredPrompt) {
      toast.info("Abre o menu do navegador (⋮) → Instalar app");
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") toast.success("Baixando o app na tua tela inicial…");
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { console.warn("[InstallBanner] localStorage write failed:", err?.message); }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed z-50 bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm animate-slide-up"
      >
        <div className="brutal-card brutal-shadow bg-[#0A0A0A]" style={{borderColor:'#CCFF00', padding:'1rem'}}>
          <div className="flex items-start gap-3">
            <div className="mt-1 shrink-0 w-11 h-11 flex items-center justify-center border-2 border-[#CCFF00] bg-[#CCFF00]/10">
              <Smartphone className="text-[#CCFF00]" size={22}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="sticker">NOVO</span>
                <span className="font-manrope text-[10px] tracking-widest uppercase text-white/50">Instala em 1 toque</span>
              </div>
              <div className="mt-2 font-bebas text-2xl leading-none tracking-tighter">
                LEVA O CRIA<span className="text-[#CCFF00]">.</span>FINANCE PRA TUA TELA
              </div>
              <div className="mt-1 font-manrope text-xs text-white/60">
                Abre offline, sem loja, sem esperar. Toca e baixa.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid="install-banner-btn"
                  onClick={install}
                  className="brutal-btn text-sm py-2 px-4 inline-flex items-center gap-2"
                >
                  <Download size={16}/> INSTALAR
                </button>
                <button
                  data-testid="install-banner-later"
                  onClick={dismiss}
                  className="font-manrope text-xs uppercase tracking-widest text-white/50 hover:text-white/80 px-2 py-2"
                >
                  Depois
                </button>
              </div>
            </div>
            <button
              data-testid="install-banner-close"
              aria-label="Fechar"
              onClick={dismiss}
              className="text-white/40 hover:text-white transition-colors"
            >
              <X size={18}/>
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 font-manrope text-[11px] text-white/40">
            <Zap size={12} className="text-[#CCFF00]"/>
            Aparece na tela inicial como app nativo
          </div>
        </div>
      </div>

      {showIOS && (
        <div data-testid="ios-install-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={()=>setShowIOS(false)}>
          <div onClick={(e)=>e.stopPropagation()} className="brutal-card brutal-shadow max-w-md w-full" style={{borderColor:'#CCFF00'}}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="text-[#CCFF00]" size={28}/>
                <span className="sticker">INSTALAR NO IPHONE</span>
              </div>
              <button data-testid="ios-close" onClick={()=>setShowIOS(false)} className="text-white/60 hover:text-white"><X size={22}/></button>
            </div>
            <h3 className="mt-4 font-bebas text-3xl tracking-tighter">Adicionar à tela inicial</h3>
            <ol className="mt-6 space-y-3 font-manrope text-white/80">
              <li className="flex gap-3"><span className="font-bebas text-[#CCFF00] text-2xl">1.</span>Toca no botão <b>Compartilhar</b> (quadrado com seta pra cima) no Safari</li>
              <li className="flex gap-3"><span className="font-bebas text-[#CCFF00] text-2xl">2.</span>Rola pra baixo e toca em <b>"Adicionar à Tela de Início"</b></li>
              <li className="flex gap-3"><span className="font-bebas text-[#CCFF00] text-2xl">3.</span>Toca em <b>Adicionar</b>. Pronto, o CRIA vai virar app!</li>
            </ol>
            <div className="mt-6 text-xs text-white/50 font-manrope">
              * Só funciona no Safari. Se estiver em outro navegador, abre no Safari primeiro.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
